/* eslint-disable react/no-unescaped-entities, react-hooks/preserve-manual-memoization --
 * v77.99.79: English copy uses apostrophes ("you've", "haven't"). The
 * useMemo-derived `lastExportRelative` / `lastExportAbsolute` strings
 * are flagged by the experimental React 19 preserve-manual-memoization
 * rule but are pure derivations — intentional.
 */
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { CalendarDays, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CalendarExportDialog } from "@/components/calendar/CalendarExportDialog";

/** Relative-time pill: "today" / "yesterday" / "3 days ago" / "2 weeks ago" / "3 months ago". */
function formatRelative(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `${w} week${w === 1 ? "" : "s"} ago`;
  }
  if (days < 365) {
    const m = Math.floor(days / 30);
    return `${m} month${m === 1 ? "" : "s"} ago`;
  }
  const y = Math.floor(days / 365);
  return `${y} year${y === 1 ? "" : "s"} ago`;
}

/**
 * v77.99.79 — Discoverable shortcut for the calendar .ics export
 * surfaced on Settings. Two states:
 *   1. Never exported yet → soft prompt ("Sync to your phone's calendar
 *      in one tap.") + the same dialog as the calendar page header.
 *   2. Has exported → "Last exported · 3 days ago" + Re-export button.
 *
 * The dialog itself is the same `CalendarExportDialog` used by the
 * calendar page — one source of truth for the export UX. Children list
 * is fetched once when the user opens the dialog, not eagerly, to keep
 * Settings render-light.
 */
export function CalendarSyncCard() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [childList, setChildList] = useState([]);

  const lastExportRelative = useMemo(
    () => formatRelative(user?.last_ics_export_at),
    [user?.last_ics_export_at],
  );
  const lastExportAbsolute = useMemo(() => {
    if (!user?.last_ics_export_at) return null;
    const d = new Date(user.last_ics_export_at);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  }, [user?.last_ics_export_at]);

  async function openDialog() {
    // Pull active children on demand so the dialog's child filter has
    // options. Cheap call — we cache it for subsequent opens.
    if (childList.length === 0) {
      try {
        const { data } = await api.get("/children?include_archived=false");
        setChildList(data || []);
      } catch {
        // Soft-fail — dialog still works with "All my children".
        setChildList([]);
      }
    }
    setOpen(true);
  }

  return (
    <section
      className="border border-[#D9E1E3] bg-white rounded-md p-6"
      data-testid="calendar-sync-card"
    >
      <h2 className="font-heading text-lg font-medium inline-flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-[#1F9FA4]" />
        Calendar sync
      </h2>
      <p className="text-sm text-[#6F828A] mt-1">
        Download a snapshot of your events as an .ics file. Open it on your
        phone or laptop to import into Google Calendar, Apple Calendar,
        or Outlook.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {lastExportRelative ? (
          <div
            className="text-sm text-[#2E3A3F] inline-flex items-center gap-2"
            data-testid="calendar-sync-last-export"
            title={lastExportAbsolute || ""}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#1F9FA4]" />
            <span>
              Last exported <span className="font-medium">{lastExportRelative}</span>
            </span>
          </div>
        ) : (
          <div
            className="text-sm text-[#6F828A] inline-flex items-center gap-2"
            data-testid="calendar-sync-never-exported"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#D9E1E3]" />
            <span>You haven't exported your calendar yet.</span>
          </div>
        )}

        <Button
          onClick={openDialog}
          variant="outline"
          className="ml-auto border-[#1F9FA4] text-[#116B6E] hover:bg-[#F0F8F8] rounded-sm"
          data-testid="calendar-sync-export-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {lastExportRelative ? "Re-export" : "Export .ics"}
        </Button>
      </div>

      <CalendarExportDialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          // Refresh `user` after a successful download so the
          // "Last exported" badge updates without a manual reload.
          if (!v && refresh) refresh();
        }}
        childList={childList}
        monthOffset={0}
      />
    </section>
  );
}
